import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import {
  ASYNC_HISTORY_FIELD_KEYS,
  CONVERSATION_EXPORT_FIELDS,
  EXPORT_FIELD_GROUPS,
  getDefaultSelectedExportKeys,
} from "../../utils/conversationExportFields";
import { runAdvancedConversationExport } from "../../utils/advancedConversationExport";

export default function AdvancedExportDialog({
  open,
  onClose,
  selectedIds = [],
  rows = [],
  exportTitle = "Resultados da Busca",
  exportFileName = "resultados_busca",
  exportContext = {},
}) {
  const [selectedKeys, setSelectedKeys] = useState(getDefaultSelectedExportKeys);
  const [format, setFormat] = useState("csv");
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);

  const selectedCount = selectedIds.length;

  useEffect(() => {
    if (!open) return;
    setSelectedKeys(getDefaultSelectedExportKeys());
    setFormat("csv");
    setExporting(false);
    setError(null);
    setProgress(null);
  }, [open]);

  const fieldsByGroup = useMemo(() => {
    const map = {};
    EXPORT_FIELD_GROUPS.forEach((g) => {
      map[g.id] = CONVERSATION_EXPORT_FIELDS.filter((f) => f.group === g.id);
    });
    return map;
  }, []);

  const toggleField = (key) => {
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleGroup = (groupId, checked) => {
    const groupKeys = (fieldsByGroup[groupId] || []).map((f) => f.key);
    setSelectedKeys((prev) => {
      const without = prev.filter((k) => !groupKeys.includes(k));
      return checked ? [...without, ...groupKeys] : without;
    });
  };

  const isGroupChecked = (groupId) => {
    const groupKeys = (fieldsByGroup[groupId] || []).map((f) => f.key);
    return groupKeys.length > 0 && groupKeys.every((k) => selectedKeys.includes(k));
  };

  const isGroupIndeterminate = (groupId) => {
    const groupKeys = (fieldsByGroup[groupId] || []).map((f) => f.key);
    const selectedInGroup = groupKeys.filter((k) => selectedKeys.includes(k));
    return selectedInGroup.length > 0 && selectedInGroup.length < groupKeys.length;
  };

  const handleExport = async () => {
    if (!selectedKeys.length) {
      setError("Selecione pelo menos um campo para exportar.");
      return;
    }

    setExporting(true);
    setError(null);
    setProgress(null);

    try {
      const result = await runAdvancedConversationExport({
        rows,
        selectedIds,
        selectedKeys,
        format,
        title: exportTitle,
        fileName: exportFileName,
        exportContext,
        onProgress: setProgress,
      });

      if (!result.ok) {
        setError("Nenhum dado para exportar.");
        return;
      }

      onClose?.();
    } catch (e) {
      setError(e?.message || "Erro ao exportar. Tente novamente.");
    } finally {
      setExporting(false);
      setProgress(null);
    }
  };

  const progressLabel =
    progress?.phase === "messages" && progress.total
      ? `Carregando mensagens (${progress.completed}/${progress.total})…`
      : null;

  return (
    <Dialog open={open} onClose={exporting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <TuneIcon color="primary" fontSize="small" />
        Exportação avançada
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Typography variant="body2" color="text.secondary">
            {selectedCount} solicitação{selectedCount === 1 ? "" : "ões"} selecionada
            {selectedCount === 1 ? "" : "s"}. Escolha o formato e os campos que deseja incluir no
            arquivo.
          </Typography>

          <FormControl>
            <FormLabel sx={{ mb: 0.5, fontSize: 13, fontWeight: 600 }}>Formato</FormLabel>
            <RadioGroup
              row
              value={format}
              onChange={(e) => setFormat(e.target.value)}
            >
              <FormControlLabel value="csv" control={<Radio size="small" />} label="CSV" />
              <FormControlLabel value="pdf" control={<Radio size="small" />} label="PDF" />
            </RadioGroup>
          </FormControl>

          {EXPORT_FIELD_GROUPS.map((group) => (
            <Box key={group.id}>
              <FormControlLabel
                sx={{ mb: 0.5, ml: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={isGroupChecked(group.id)}
                    indeterminate={isGroupIndeterminate(group.id)}
                    onChange={(e) => toggleGroup(group.id, e.target.checked)}
                    disabled={exporting}
                  />
                }
                label={
                  <Typography variant="subtitle2" component="span">
                    {group.label}
                  </Typography>
                }
              />
              <Stack sx={{ pl: 3.5 }}>
                {(fieldsByGroup[group.id] || []).map((field) => (
                  <FormControlLabel
                    key={field.key}
                    sx={{ ml: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedKeys.includes(field.key)}
                        onChange={() => toggleField(field.key)}
                        disabled={exporting}
                      />
                    }
                    label={field.label}
                  />
                ))}
              </Stack>
            </Box>
          ))}

          {selectedKeys.some((k) => ASYNC_HISTORY_FIELD_KEYS.includes(k)) && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Campos de histórico (anotações, registros e mensagens) podem levar mais tempo — o
              conteúdo será buscado de cada solicitação selecionada.
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {exporting && (
            <Box>
              <LinearProgress />
              {progressLabel && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                  {progressLabel}
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={exporting} sx={{ textTransform: "none" }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleExport}
          disabled={exporting || !selectedKeys.length}
          startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ textTransform: "none" }}
        >
          {exporting ? "Exportando…" : "Exportar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
